import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import {
  searchVehicle,
  searchEmployee,
  validatePaymentReference,
  validateVoucher,
  list_personal_wallets,
  get_personal_wallet_balance,
} from "../../api/otogas";
import CustomersContext from "../../context/otogas/customers/customersContext";
import debounce from "lodash.debounce";
import { set } from "date-fns";

// Config for payment types with rules for each
const paymentTypeConfig = {
  0: {
    requiresVehicle: true,
    requiresPaymentReference: true,
    label: "Mpesa",
    requiresEmployee: false,
    requiresPaymentValidation: true,
  },
  1: {
    requiresVehicle: true,
    requiresWalletCheck: true,
    label: "Wallet",
    requiresEmployee: false,
    requiresPaymentValidation: false,
  },
  2: {
    requiresVehicle: true,
    label: "new Conversions",
    requiresEmployee: false,
    requiresPaymentValidation: false,
  },
  3: {
    requiresEmployee: true,
    label: "Operational loss",
    requiresVehicle: false,
    requiresPaymentValidation: false,
  },
  4: {
    requiresEmployee: true,
    label: "Bank transfer",
    requiresVehicle: false,
    requiresPaymentValidation: false,
  },
  5: {
    requiresEmployee: true,
    requiresPaymentReference: true,
    label: "Salary",
    requiresVehicle: false,
    requiresPaymentValidation: true,
  },
  6: {
    requiresPaymentReference: true,
    label: "Insurance",
    requiresVehicle: false,
    requiresEmployee: true,
    requiresPaymentValidation: false,
  },
  7: {
    requiresPaymentReference: true,
    label: "Voucher",
    requiresVehicle: true,
    requiresEmployee: false,
    requiresPaymentValidation: true,
  },
  8: {
    requiresEmployee: true,
    label: "Calibration",
    requiresPaymentValidation: false,
  },
  9: {
    requiresEmployee: true,
    label: "Compesational fuel",
    requiresPaymentValidation: false,
  },
  10: {
    requiresEmployee: true,
    label: "Batch Voucher",
    requiresVehicle: false,
    requiresPaymentValidation: false,
  },
  11: {
    requiresEmployee: false,
    label: "Personal Wallet",
    requiresVehicle: true,
    requiresPaymentValidation: false,
    requiresWalletOwnerCheck: true,
  },
};

// ===== BAND HELPERS =====

// Find the band whose range contains the given quantity
const getBandForQuantity = (bands, quantity) => {
  if (!Array.isArray(bands) || !bands.length) return null;
  const qty = parseFloat(quantity);
  if (isNaN(qty) || qty <= 0) return null;

  return (
    bands.find((band) => {
      const min = band.minLitres ?? 0;
      const max = band.maxLitres;

      if (max === null || max === undefined) {
        // Open-ended band: minLitres and above
        return qty >= min;
      }

      return qty >= min && qty <= max;
    }) || null
  );
};

// Decide which price to use based on base price + bands for a given quantity
// ✅ Band applies only if it's LOWER than base price
const getPriceAndBandForQuantity = (basePrice, bands, quantity) => {
  const matchedBand = getBandForQuantity(bands, quantity);

  if (
    matchedBand &&
    matchedBand.bandPrice != null &&
    basePrice != null &&
    matchedBand.bandPrice < basePrice
  ) {
    return { priceToApply: matchedBand.bandPrice, matchedBand };
  }

  return { priceToApply: basePrice, matchedBand: null };
};

// For vouchers: decide band/price from amount directly
// ✅ Band applies only if it's LOWER than base price
const getBandPriceForVoucherAmount = (amount, basePrice, bands) => {
  if (!basePrice || !Array.isArray(bands) || bands.length === 0) {
    return { priceToUse: basePrice, matchedBand: null };
  }

  // 1) Start with base price to estimate a quantity
  const qtyBase = amount / basePrice;

  const findBand = (qty) =>
    bands.find((band) => {
      const min = band.minLitres ?? 0;
      const max = band.maxLitres;

      if (max === null || max === undefined) {
        return qty >= min;
      }
      return qty >= min && qty <= max;
    }) || null;

  let band = findBand(qtyBase);

  // If no band, or band has no price, or band price isn't a "deal", use base
  if (!band || band.bandPrice == null || !(band.bandPrice < basePrice)) {
    return { priceToUse: basePrice, matchedBand: null };
  }

  // 2) Recompute with band price
  const qtyBand = amount / band.bandPrice;
  const verifyBand = findBand(qtyBand);

  if (
    verifyBand &&
    verifyBand.bandPrice != null &&
    verifyBand.bandPrice < basePrice
  ) {
    return { priceToUse: band.bandPrice, matchedBand: verifyBand };
  }

  // Fallback to base price if it somehow doesn't fit
  return { priceToUse: basePrice, matchedBand: null };
};

const VarianceClearanceModal = ({ show, handleClose, selectedVariance }) => {
  const [paymentType, setPaymentType] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [amountPayable, setAmountPayable] = useState(0);
  const [paymentReference, setPaymentReference] = useState("");
  const [comment, setComment] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [vehicleDetails, setVehicleDetails] = useState(null);

  // price = applied computed price (base or band-deal)
  const [price, setPrice] = useState(null);

  // base price (API price before band adjustment)
  const [basePrice, setBasePrice] = useState(null);

  // bands from API + the one currently applied
  const [activeBandPrices, setActiveBandPrices] = useState([]);
  const [appliedBand, setAppliedBand] = useState(null);

  const [walletBalance, setWalletBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentReferenceValid, setPaymentReferenceValid] = useState(null);
  const [loading, setLoading] = useState(false);

  // Error states
  const [vehicleError, setVehicleError] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [paymentReferenceError, setPaymentReferenceError] = useState("");
  const [walletOptions, setWalletOptions] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // ===== Price override (Approval UI only) =====
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overridePrice, setOverridePrice] = useState("");

  const {
    vehicles,
    fetchVehicles,
    payment_types: paymentTypes,
    listPaymentTypes,
    addSale,
  } = useContext(CustomersContext);

  const [vehicleOptions, setVehicleOptions] = useState([]);

  const hasValidOverride = () => {
    const o = parseFloat(overridePrice);
    return overrideEnabled && overridePrice !== "" && !isNaN(o);
  };

  // ✅ FINAL RULE:
  // - Override wins (always) when enabled + valid
  // - Otherwise use computed (base or band-deal)
  const getEffectiveUnitPrice = (computedPrice) => {
    if (hasValidOverride()) return parseFloat(overridePrice);
    return computedPrice;
  };

  useEffect(() => {
    listPaymentTypes();
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Automatically fetch the price based on payment type
    if (paymentType || paymentType === 0) {
      const config = paymentTypeConfig[paymentType];
      if (config?.requiresEmployee && selectedVariance?.userCode) {
        fetchPriceForUser(selectedVariance.userCode);
      } else if (config?.requiresVehicle && vehicleDetails?.vehicleCode) {
        fetchPriceForVehicle(vehicleDetails.vehicleCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, selectedVariance?.userCode, vehicleDetails?.vehicleCode]);

  const fetchPriceForUser = async (userCode) => {
    setIsLoading(true);
    try {
      const priceData = await searchEmployee(userCode);
      if (priceData) {
        setPrice(priceData.price);
        setBasePrice(priceData.price);
      } else {
        alert("Price not found or error occurred.");
      }
    } catch (error) {

    }
    setIsLoading(false);
  };


  const searchWalletUser = async (phoneNumber) => {
    setIsLoading(true);
    try {
      const res = await list_personal_wallets(
        "", // walletId
        phoneNumber,
        "", // name
        "", // identificationNumber
        1, // page
        10 // pageSize
      );

      if (res?.responseCode === 1) {
        const options = res.responseObject.data.map((wallet) => ({
          label: `${wallet.firstName} ${wallet.lastName} (${wallet.phoneNumber})`,
          value: wallet.walletId,
          credit: wallet.credit,
          userCode: wallet.userCode,
        }));

        setWalletOptions(options);
      } else {
        setWalletOptions([]);
      }
    } catch (error) {
      setWalletOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPriceForVehicle = async (vehicleCode) => {
    setIsLoading(true);
    try {
      const vehicleData = await searchVehicle(
        selectedVariance?.stationCode,
        vehicleDetails.vehicleRegistrationNumber,
        selectedVariance.shiftNumber
      );

      if (vehicleData?.responseObject) {
        const priceFromApi = vehicleData.responseObject.price;
        const vehicleWalletBal =
          vehicleData.responseObject.walletBalance ?? null;
        const bandsFromApi = vehicleData.responseObject.activeBandPrices || [];

        if (priceFromApi != null) {
          setBasePrice(priceFromApi);
          setPrice(priceFromApi);
          setActiveBandPrices(bandsFromApi);
          setAppliedBand(null);
          setOverrideEnabled(false);
          setOverridePrice("");

          // Only override wallet balance for VEHICLE WALLET (type 1)
          if (paymentType === 1) {
            setWalletBalance(vehicleWalletBal);
          }
        } else {
          setVehicleError("Price not found or error occurred.");
        }
      } else {
        setVehicleError("Error fetching vehicle details.");
      }
    } catch (error) {
      setVehicleError("Error fetching price.");
    } finally {
      setIsLoading(false);
    }
  };

  // Recalculate applied price + total when quantity changes (for vehicle-based, non-voucher flows)
  useEffect(() => {
    const config = paymentTypeConfig[paymentType] || {};
    const numericQty = parseFloat(quantity);

    // Skip all band re-calcs for voucher: handled in voucher validation
    if (paymentType === 7) return;

    if (
      !config.requiresVehicle ||
      basePrice == null ||
      isNaN(numericQty) ||
      numericQty <= 0
    ) {
      if (config.requiresVehicle) {
        setAmountPayable(0);
        setQuantityError("");
        setAppliedBand(null);
        if (basePrice != null) {
          setPrice(basePrice);
        } else {
          setPrice(null);
        }
      }
      return;
    }

    const { priceToApply, matchedBand } = getPriceAndBandForQuantity(
      basePrice,
      activeBandPrices,
      numericQty
    );

    // computed price (base or band-deal)
    setPrice(priceToApply);

    // ✅ Override wins: if override is active, do not show band as applied
    if (hasValidOverride()) setAppliedBand(null);
    else setAppliedBand(matchedBand);

    // totals use effective price (override if enabled, else computed)
    const unitPriceForTotals = getEffectiveUnitPrice(priceToApply);
    const total = unitPriceForTotals * numericQty;
    setAmountPayable(total.toFixed(2));

    if ((paymentType === 1 || paymentType === 11) && walletBalance !== null) {
      if (total > walletBalance) {
        setQuantityError("Entered quantity exceeds wallet balance");
        setQuantity(0);
        setAmountPayable(0);
      } else {
        setQuantityError("");
      }
    } else {
      setQuantityError("");
    }
  }, [
    quantity,
    basePrice,
    walletBalance,
    paymentType,
    activeBandPrices,
    overrideEnabled,
    overridePrice,
  ]);

  const validatePayment = async () => {
    setPaymentReferenceError("");
    setPaymentReferenceValid(null);
    setIsLoading(true);



    if (paymentType === 7) {
      // Voucher validation with band-deal logic + override wins
      try {
        const response = await validateVoucher(paymentReference);
        if (response && response.responseCode === 1) {
          const { amount } = response.responseObject;

          const effectiveBasePrice = basePrice ?? price;

          const { priceToUse, matchedBand } = getBandPriceForVoucherAmount(
            amount,
            effectiveBasePrice,
            activeBandPrices
          );

          // ✅ Override wins for voucher too
          const unitPriceForVoucher = getEffectiveUnitPrice(priceToUse);

          // Display effective unit price
          setPrice(unitPriceForVoucher);

          // Override active => no band applied
          if (hasValidOverride()) setAppliedBand(null);
          else setAppliedBand(matchedBand);

          // Quantity = amount / effective unit price
          const qty = amount / unitPriceForVoucher;
          setQuantity(qty.toFixed(2));

          // Total payable is voucher amount
          setAmountPayable(amount);

          setPaymentReferenceValid(response.responseMessage);
        } else {
          setPaymentReferenceError(response.responseMessage);
        }
      } catch (error) {
        setPaymentReferenceError("Error validating voucher.");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Mpesa / Salary / etc
      try {
        const response = await validatePaymentReference(
          selectedVariance.dispenserCode,
          paymentReference
        );

        if (response && response.responseCode === 1) {
          const parsedAmountPayable = parseFloat(amountPayable);
          const balanceUsage = parseFloat(response.responseObject.usageBalance);

          if (parsedAmountPayable <= balanceUsage) {
            setPaymentReferenceValid(response.responseMessage);
          } else {
            setPaymentReferenceError(response.responseMessage);
          }
        } else {
          setPaymentReferenceError(response.responseMessage);
        }
      } catch (error) {
        setPaymentReferenceValid("Error validating payment.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    let hasErrors = false;

    const config = paymentTypeConfig[paymentType];

    // If employee is required, use userCode as vehicleCode
    const vehicleCode = config?.requiresEmployee
      ? selectedVariance?.userCode
      : vehicleDetails?.vehicleCode;

    if (!vehicleCode && config?.requiresVehicle) {
      setVehicleError("Vehicle registration is required.");
      hasErrors = true;
    } else {
      setVehicleError("");
    }

    if (quantity <= 0 && paymentType !== 3) {
      setQuantityError("Quantity must be greater than 0.");
      hasErrors = true;
    } else {
      setQuantityError("");
    }

    if (config?.requiresPaymentReference && paymentReference.trim() === "") {
      setPaymentReferenceError("Payment reference is required.");
      hasErrors = true;
    } else if (config?.requiresPaymentValidation && !paymentReferenceValid) {
      setPaymentReferenceError("Please validate the payment reference.");
      hasErrors = true;
    } else {
      setPaymentReferenceError("");
    }

    if (hasErrors) return;

    // ✅ FINAL: payload price must match effective price used (override wins, else computed base/band)
    const effectiveUnitPriceForPayload = parseFloat(
      getEffectiveUnitPrice(price ?? 0)
    );

    const payload = {
      walletId: selectedWallet?.value || "",
      vehicleCode,
      paymentTypeCode: paymentType,
      nozzleCode: selectedVariance?.nozzleCode,
      shiftNumber: selectedVariance?.shiftNumber,
      quantity: parseFloat(quantity),
      price: effectiveUnitPriceForPayload,
      dispenserCode: selectedVariance?.dispenserCode,
      comment,
      paymentDetails: [
        {
          transactionReference: paymentReference,
          transactionAmount: amountPayable,
        },
      ],
    };

    try {
      const response = await addSale(payload);
      if (response && response.error) {
        setPaymentReferenceError(
          "Error submitting form: " + response.error.message
        );
      } else {
        resetForm();
        handleClose();
      }
    } catch (error) {
      setPaymentReferenceError(
        "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setVehicleDetails(null);
    setQuantity(0);
    setAmountPayable(0);
    setPaymentReference("");
    setComment("");
    setPrice(0);
    setBasePrice(null);
    setActiveBandPrices([]);
    setAppliedBand(null);
    setWalletBalance(null);
    setPaymentReferenceValid(null);
    setIsLoading(false);
    setPaymentReferenceError("");
    setVehicleError("");
    setQuantityError("");
    setVehicleOptions([]);
    setSelectedWallet(null);
    setOverrideEnabled(false);
    setOverridePrice("");
  };

  const handleChange = (selectedOption) => {
    setVehicleDetails({
      vehicleCode: selectedOption.value,
      vehicleRegistrationNumber: selectedOption.label,
    });
  };

  const debouncedFetchVehicles = debounce((regNo) => {
    fetchVehicles(1, 10, "", regNo, "", "", true);
  }, 500);

  useEffect(() => {
    if (vehicles) {
      const options = vehicles.map((vehicle) => ({
        value: vehicle.vehicleCode,
        label: vehicle.vehicleRegistrationNumber,
      }));
      setVehicleOptions(options);
    }
  }, [vehicles]);


  const hasBands =
    Array.isArray(activeBandPrices) && activeBandPrices.length > 0;

  return (
    <Modal
      show={show}
      onHide={() => {
        resetForm();
        handleClose();
      }}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Variance Clearance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="paymentType">
            <Form.Label>Select Payment Type</Form.Label>
            <Select
              options={paymentTypes.map((type) => ({
                value: type.paymentTypeId,
                label: type.paymentTypeName.replace(/_/g, " "),
              }))}
              onChange={(selectedOption) => {
                resetForm();
                setPaymentType(selectedOption.value);
              }}
              placeholder="Select Payment Type"
            />
          </Form.Group>

          {paymentTypeConfig[paymentType]?.requiresWalletOwnerCheck && (
            <Form.Group controlId="walletSearch">
              <Form.Label>Search Personal Wallet</Form.Label>
              <Select
                options={walletOptions}
                onInputChange={(inputValue) => {
                  if (inputValue.length >= 3) {
                    searchWalletUser(inputValue);
                  }
                }}
                onChange={async (selectedOption) => {
                  setSelectedWallet(selectedOption);
                  setIsLoading(true);
                  try {
                    const res = await get_personal_wallet_balance(
                      selectedOption.value
                    );
                    if (res?.responseCode) {
                      setWalletBalance(res.responseObject);
                    } else {
                      setWalletBalance(0);
                    }
                  } catch (error) {
                    setWalletBalance(0);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                placeholder="Type phone number..."
                isClearable
                isLoading={isLoading}
              />
            </Form.Group>
          )}

          {paymentType === 11 && walletBalance !== null && (
            <Form.Group controlId="personalWalletBalance" className="mt-2">
              <Form.Label>Personal Wallet Details</Form.Label>
              <p>
                <strong>Wallet Balance:</strong> {walletBalance}
              </p>
            </Form.Group>
          )}

          {/* Vehicle select */}
          {paymentTypeConfig[paymentType]?.requiresVehicle && (
            <Form.Group controlId="vehicleRegistration">
              <Form.Label>Vehicle Registration</Form.Label>
              <Select
                options={vehicleOptions}
                onChange={handleChange}
                onInputChange={(inputValue) =>
                  debouncedFetchVehicles(inputValue)
                }
                placeholder="Select Vehicle"
                value={vehicleOptions.find(
                  (option) => option.value === vehicleDetails?.vehicleCode
                )}
                isClearable
              />
              {vehicleError && (
                <Form.Text className="text-danger">{vehicleError}</Form.Text>
              )}
              {isLoading && <Spinner animation="border" size="sm" />}
            </Form.Group>
          )}

          {/* ===== Price Override (Approval) ===== */}
          {paymentTypeConfig[paymentType]?.requiresVehicle &&
            vehicleDetails?.vehicleCode && (
              <Form.Group controlId="priceOverride" className="mt-2">
                <Form.Check
                  type="checkbox"
                  label="Override unit price (Approval)"
                  checked={overrideEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setOverrideEnabled(checked);

                    // default the override input to current computed price
                    if (checked) setOverridePrice(String(price ?? ""));
                    else setOverridePrice("");
                  }}
                />

                {overrideEnabled && (
                  <Form.Control
                    className="mt-2"
                    type="number"
                    step="0.01"
                    value={overridePrice}
                    onChange={(e) => setOverridePrice(e.target.value)}
                    placeholder="Enter override unit price"
                  />
                )}

                {/* display only */}
                {overrideEnabled && overridePrice !== "" && (
                  <Form.Text className="text-muted d-block mt-1">
                    Totals will use override unit price:{" "}
                    <strong>{overridePrice}</strong>
                  </Form.Text>
                )}
              </Form.Group>
            )}

          {/* Wallet Details Section */}
          {paymentTypeConfig[paymentType]?.requiresWalletCheck &&
            walletBalance !== null && (
              <Form.Group controlId="walletDetails">
                <Form.Label>Vehicle Wallet Details</Form.Label>
                <p>
                  <strong>Base price per unit:</strong>{" "}
                  {basePrice != null ? basePrice : price}
                </p>
                {appliedBand && (
                  <p>
                    <strong>Band price applied:</strong> {price}{" "}
                    <small>
                      ({appliedBand.minLitres} -{" "}
                      {appliedBand.maxLitres || "above"} L)
                    </small>
                  </p>
                )}
                {overrideEnabled && overridePrice !== "" && (
                  <p>
                    <strong>Override unit price:</strong> {overridePrice}
                  </p>
                )}
                <p>
                  <strong>Effective unit price used:</strong>{" "}
                  {getEffectiveUnitPrice(price ?? "")}
                </p>
                <p>
                  <strong>Wallet Balance:</strong> {walletBalance}
                </p>
              </Form.Group>
            )}

          {/* Active Band Prices display */}
          {paymentTypeConfig[paymentType]?.requiresVehicle && hasBands && (
            <Form.Group controlId="bandPrices">
              <Form.Label>Active Band Prices</Form.Label>
              <ul className="mb-0">
                {activeBandPrices
                  .slice()
                  .sort((a, b) => a.minLitres - b.minLitres)
                  .map((band, idx) => {
                    const isApplied =
                      appliedBand &&
                      band.minLitres === appliedBand.minLitres &&
                      (band.maxLitres ?? null) ===
                      (appliedBand.maxLitres ?? null) &&
                      band.bandPrice === appliedBand.bandPrice;

                    return (
                      <li key={idx}>
                        {band.minLitres} - {band.maxLitres || "above"} L @{" "}
                        {band.bandPrice}
                        {isApplied && <strong> (applied)</strong>}
                      </li>
                    );
                  })}
              </ul>
            </Form.Group>
          )}

          {/* Employee Details */}
          {paymentTypeConfig[paymentType]?.requiresEmployee &&
            selectedVariance && (
              <div>
                <p>
                  <strong>Name:</strong> {selectedVariance?.name || "N/A"}
                </p>
                <p>
                  <strong>User Code:</strong>{" "}
                  {selectedVariance?.userCode || "N/A"}
                </p>
                {isLoading && <Spinner animation="border" size="sm" />}
              </div>
            )}

          {/* Quantity */}
          {price && paymentType === 7 ? (
            <Form.Group controlId="quantity">
              <Form.Label>Quantity</Form.Label>
              <Form.Control type="text" value={quantity} disabled />
              <Form.Text className="text-muted">
                Quantity is determined by the voucher and effective unit price.
              </Form.Text>
            </Form.Group>
          ) : (
            price && (
              <Form.Group controlId="quantity">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="text"
                  value={quantity}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    const parsedValue = parseFloat(value);

                    const unitPriceForTotals = getEffectiveUnitPrice(price);

                    if (paymentType === 3) {
                      if (value === "-" || value === "" || !isNaN(parsedValue)) {
                        setQuantityError("");
                        setQuantity(value);
                        setAmountPayable(
                          !isNaN(parsedValue)
                            ? (parsedValue * unitPriceForTotals).toFixed(2)
                            : "0"
                        );
                      } else {
                        setQuantityError("Invalid input");
                      }
                    } else if (!isNaN(parsedValue) && parsedValue >= 0) {
                      setQuantityError("");
                      setQuantity(value);
                      setAmountPayable(
                        (parsedValue * unitPriceForTotals).toFixed(2)
                      );
                    } else {
                      setQuantityError("Quantity must be greater than 0");
                      setQuantity("");
                      setAmountPayable("0");
                    }
                  }}
                  required
                />
                {quantityError && (
                  <Form.Text className="text-danger">{quantityError}</Form.Text>
                )}
              </Form.Group>
            )
          )}

          {amountPayable > 0 && (
            <Form.Group controlId="totalAmount">
              <Form.Label>Total Payable Amount</Form.Label>
              <p>
                <strong>{amountPayable}</strong>
              </p>
            </Form.Group>
          )}

          {/* Payment Reference */}
          {paymentTypeConfig[paymentType]?.requiresPaymentReference && (
            <Form.Group controlId="paymentReference">
              <Form.Label>Payment Reference</Form.Label>
              <Form.Control
                type="text"
                value={paymentReference}
                onChange={(e) => {
                  setPaymentReference(e.target.value);
                  if (paymentReferenceValid !== null) {
                    setPaymentReferenceValid(null);
                  }
                }}
                required
              />

              {paymentReferenceError && (
                <div className="mt-2">
                  <Form.Text className="text-danger">
                    {paymentReferenceError}
                  </Form.Text>
                </div>
              )}

              {paymentReferenceValid && (
                <div className="mt-2">
                  <Form.Text className="text-success">
                    {paymentReferenceValid}
                  </Form.Text>
                </div>
              )}

              {paymentType !== null &&
                paymentType !== undefined &&
                paymentTypeConfig[paymentType]?.requiresPaymentValidation && (
                  <div className="mt-3">
                    <Button
                      onClick={validatePayment}
                      disabled={isLoading || paymentReferenceValid !== null}
                    >
                      {isLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Validate Payment"
                      )}
                    </Button>
                  </div>
                )}
            </Form.Group>
          )}

          {/* Comment */}
          <Form.Group controlId="comment">
            <Form.Label>Comment</Form.Label>
            <Form.Control
              as="textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required
            />
          </Form.Group>

          {/* Submit Button */}
          <Button
            variant="primary"
            type="submit"
            disabled={!comment || isLoading || !quantity}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default VarianceClearanceModal;
